/**
 * Executable contract for every POS client.  This module deliberately contains
 * policy and state transitions only: clients may cache facts and commands, but
 * never become a second business authority.
 */
export const CLIENT_PLATFORM_CONTRACT_VERSION = "1.0.0" as const;
export const CLIENT_PLATFORM_MIN_SERVER_VERSION = "1.0.0" as const;

export const clientChannels = [
  "WEB",
  "DESKTOP",
  "MOBILE_POS",
  "SCANNER",
  "DELIVERY_AGENT",
] as const;
export type ClientChannel = (typeof clientChannels)[number];

export const clientOperations = [
  "SALE_QUOTE",
  "SALE_ACCEPT",
  "SALE_RETURN",
  "PAYMENT_CAPTURE",
  "STOCK_READ",
  "PROCUREMENT_RECEIVE",
  "MASTER_READ",
  "REPORT_READ",
  "PRINT",
  "BARCODE_SCAN",
  "DELIVERY_ASSIGN",
  "DELIVERY_PICKUP",
  "DELIVERY_CUSTODY_SCAN",
  "DELIVERY_OTP_POD",
  "DELIVERY_FAILURE",
  "DELIVERY_COD",
  "DELIVERY_RETURN_TO_STORE",
] as const;
export type ClientOperation = (typeof clientOperations)[number];

export type OfflineDecision = "ALLOW" | "DENY" | "REQUIRE_ONLINE";

export interface ClientCapability {
  online: boolean;
  offline: OfflineDecision;
  canonicalEndpoint: string;
  requiredPermissions: readonly string[];
}

const deny = (canonicalEndpoint: string): ClientCapability => ({
  online: false,
  offline: "DENY",
  canonicalEndpoint,
  requiredPermissions: [],
});

const posCapabilities: Record<ClientOperation, ClientCapability> = {
  SALE_QUOTE: {
    online: true,
    offline: "ALLOW",
    canonicalEndpoint: "/product-revival/sales/quote",
    requiredPermissions: ["sales.create"],
  },
  SALE_ACCEPT: {
    online: true,
    offline: "ALLOW",
    canonicalEndpoint: "/product-revival/sales/accept",
    requiredPermissions: ["sales.create"],
  },
  SALE_RETURN: {
    online: true,
    offline: "REQUIRE_ONLINE",
    canonicalEndpoint: "/product-revival/sales/returns",
    requiredPermissions: ["sales.return"],
  },
  PAYMENT_CAPTURE: {
    online: true,
    offline: "REQUIRE_ONLINE",
    canonicalEndpoint: "/payment-operations/capture",
    requiredPermissions: ["payments.capture"],
  },
  STOCK_READ: {
    online: true,
    offline: "ALLOW",
    canonicalEndpoint: "/product-revival/inventory/search",
    requiredPermissions: ["inventory.read"],
  },
  PROCUREMENT_RECEIVE: {
    online: true,
    offline: "REQUIRE_ONLINE",
    canonicalEndpoint: "/procurement-operations/goods-receipts",
    requiredPermissions: ["procurement.receive"],
  },
  MASTER_READ: {
    online: true,
    offline: "ALLOW",
    canonicalEndpoint: "/master-data",
    requiredPermissions: ["catalog.read"],
  },
  REPORT_READ: {
    online: true,
    offline: "DENY",
    canonicalEndpoint: "/reports",
    requiredPermissions: ["reports.basic"],
  },
  PRINT: {
    online: true,
    offline: "ALLOW",
    canonicalEndpoint: "/product-revival/print-jobs",
    requiredPermissions: ["sales.read"],
  },
  BARCODE_SCAN: {
    online: true,
    offline: "ALLOW",
    canonicalEndpoint: "/product-revival/inventory/search",
    requiredPermissions: ["catalog.read"],
  },
  DELIVERY_ASSIGN: deny("/delivery/assign"),
  DELIVERY_PICKUP: deny("/delivery/pickup"),
  DELIVERY_CUSTODY_SCAN: deny("/delivery/custody-scan"),
  DELIVERY_OTP_POD: deny("/delivery/pod"),
  DELIVERY_FAILURE: deny("/delivery/failure"),
  DELIVERY_COD: deny("/delivery/cod"),
  DELIVERY_RETURN_TO_STORE: deny("/delivery/return-to-store"),
};

const scannerCapabilities: Record<ClientOperation, ClientCapability> =
  Object.fromEntries(
    clientOperations.map((operation) => [
      operation,
      deny(posCapabilities[operation].canonicalEndpoint),
    ]),
  ) as Record<ClientOperation, ClientCapability>;
scannerCapabilities.STOCK_READ = posCapabilities.STOCK_READ;
scannerCapabilities.BARCODE_SCAN = posCapabilities.BARCODE_SCAN;

const deliveryCapabilities: Record<ClientOperation, ClientCapability> =
  Object.fromEntries(
    clientOperations.map((operation) => [
      operation,
      deny(posCapabilities[operation].canonicalEndpoint),
    ]),
  ) as Record<ClientOperation, ClientCapability>;
for (const operation of [
  "DELIVERY_PICKUP",
  "DELIVERY_CUSTODY_SCAN",
  "DELIVERY_OTP_POD",
  "DELIVERY_FAILURE",
  "DELIVERY_COD",
  "DELIVERY_RETURN_TO_STORE",
] as const) {
  deliveryCapabilities[operation] = {
    online: true,
    offline: operation === "DELIVERY_CUSTODY_SCAN" ? "ALLOW" : "REQUIRE_ONLINE",
    canonicalEndpoint: posCapabilities[operation].canonicalEndpoint,
    requiredPermissions: [
      `delivery.${operation
        .toLowerCase()
        .replace(/^delivery_/, "")
        .replace(/_/g, ".")}`,
    ],
  };
}

export const clientCapabilityMatrix: Record<
  ClientChannel,
  Record<ClientOperation, ClientCapability>
> = {
  WEB: posCapabilities,
  DESKTOP: posCapabilities,
  MOBILE_POS: posCapabilities,
  SCANNER: scannerCapabilities,
  DELIVERY_AGENT: deliveryCapabilities,
};

export class UnsupportedClientOperationError extends Error {
  readonly code = "CLIENT_OPERATION_UNSUPPORTED";
  constructor(
    readonly channel: ClientChannel,
    readonly operation: ClientOperation,
    readonly mode: "ONLINE" | "OFFLINE",
  ) {
    super(
      `${operation} is explicitly unsupported for ${channel} in ${mode.toLowerCase()} mode`,
    );
  }
}

export function requireClientCapability(
  channel: ClientChannel,
  operation: ClientOperation,
  mode: "ONLINE" | "OFFLINE",
) {
  const capability = clientCapabilityMatrix[channel][operation];
  const supported =
    mode === "ONLINE" ? capability.online : capability.offline === "ALLOW";
  if (!supported)
    throw new UnsupportedClientOperationError(channel, operation, mode);
  return capability;
}

export interface CanonicalClientTransport {
  request<T>(input: {
    method: "GET" | "POST";
    path: string;
    body?: Readonly<Record<string, unknown>>;
    headers: Readonly<Record<string, string>>;
  }): Promise<T>;
}

/** The only write dispatcher clients should use; every mutation is an ordinary canonical command. */
export async function executeCanonicalClientOperation<T>(
  transport: CanonicalClientTransport,
  input: {
    channel: ClientChannel;
    operation: ClientOperation;
    mode: "ONLINE" | "OFFLINE";
    payload?: Readonly<Record<string, unknown>>;
    idempotencyKey: string;
    requestHash: string;
    correlationId: string;
    clientVersion: string;
    deviceId?: string;
  },
): Promise<T> {
  const capability = requireClientCapability(
    input.channel,
    input.operation,
    input.mode,
  );
  if (input.mode === "OFFLINE")
    throw new Error("OFFLINE_COMMAND_MUST_BE_QUEUED");
  return transport.request<T>({
    method: input.operation.endsWith("_READ") ? "GET" : "POST",
    path: capability.canonicalEndpoint,
    body: input.payload,
    headers: {
      "idempotency-key": input.idempotencyKey,
      "x-request-hash": input.requestHash,
      "x-correlation-id": input.correlationId,
      "x-client-version": input.clientVersion,
      ...(input.deviceId ? { "x-device-id": input.deviceId } : {}),
    },
  });
}

export interface ClientContractManifest {
  contractVersion: string;
  minimumServerVersion: string;
  supportedServerMajor: number;
  channel: ClientChannel;
}

const parseVersion = (value: string) => {
  const match = /^(\d+)\.(\d+)\.(\d+)$/.exec(value);
  if (!match) throw new Error(`INVALID_SEMVER:${value}`);
  return match.slice(1).map(Number) as [number, number, number];
};

export function assertClientServerCompatibility(
  manifest: ClientContractManifest,
  serverVersion: string,
): void {
  const [serverMajor, serverMinor, serverPatch] = parseVersion(serverVersion);
  const [minimumMajor, minimumMinor, minimumPatch] = parseVersion(
    manifest.minimumServerVersion,
  );
  if (
    serverMajor !== manifest.supportedServerMajor ||
    serverMajor < minimumMajor ||
    (serverMajor === minimumMajor && serverMinor < minimumMinor) ||
    (serverMajor === minimumMajor &&
      serverMinor === minimumMinor &&
      serverPatch < minimumPatch)
  ) {
    throw new Error(
      `CLIENT_SERVER_CONTRACT_INCOMPATIBLE:${manifest.contractVersion}:${serverVersion}`,
    );
  }
}

export interface ProjectionStamp {
  aggregateVersion: number;
  projectionVersion: number;
  correlationId: string;
  observedAt: string;
}

export function canonicalQueryKey(
  scope: { tenantId: string; storeId: string },
  resource: string,
  stamp?: ProjectionStamp,
) {
  return [
    "canonical",
    scope.tenantId,
    scope.storeId,
    resource,
    stamp?.projectionVersion ?? "current",
  ] as const;
}

export function shouldInvalidateProjection(
  previous: ProjectionStamp | undefined,
  incoming: ProjectionStamp,
): boolean {
  return (
    !previous ||
    incoming.projectionVersion > previous.projectionVersion ||
    incoming.correlationId === previous.correlationId
  );
}

export interface TrustedDeviceContext {
  deviceId: string;
  tenantId: string;
  storeId: string;
  userId: string;
  counterId?: string;
  registeredAt: string;
  revokedAt?: string;
  offlineEntitledUntil?: string;
  clientClock: string;
}

export function assertTrustedDevice(
  context: TrustedDeviceContext,
  expected: { tenantId: string; storeId: string; userId: string },
  now = new Date(),
  maxClockSkewMs = 5 * 60_000,
) {
  if (
    context.tenantId !== expected.tenantId ||
    context.storeId !== expected.storeId ||
    context.userId !== expected.userId
  )
    throw new Error("DEVICE_SCOPE_MISMATCH");
  if (context.revokedAt) throw new Error("DEVICE_REVOKED");
  if (
    Math.abs(new Date(context.clientClock).getTime() - now.getTime()) >
    maxClockSkewMs
  )
    throw new Error("DEVICE_CLOCK_SKEW");
  if (
    !context.offlineEntitledUntil ||
    new Date(context.offlineEntitledUntil) < now
  )
    throw new Error("DEVICE_OFFLINE_ENTITLEMENT_EXPIRED");
}

export type ComplianceDomain =
  | "PROHIBITION"
  | "LICENCE"
  | "SCHEDULE_H2"
  | "NPPA_PRICE"
  | "GST_PROVIDER";
export interface OfflineSnapshot {
  snapshotId: string;
  encrypted: true;
  tenantId: string;
  storeId: string;
  issuedAt: string;
  expiresAt: string;
  versions: Readonly<Record<ComplianceDomain, string>>;
  freshnessUntil: Readonly<Record<ComplianceDomain, string>>;
  emergencyKillListVersion: string;
  products: readonly unknown[];
  packs: readonly unknown[];
  prices: readonly unknown[];
  taxRules: readonly unknown[];
  complianceRules: readonly unknown[];
  parties: readonly unknown[];
  lots: readonly unknown[];
  authorizationLimits: readonly unknown[];
}

export function assertOfflineSnapshot(
  snapshot: OfflineSnapshot,
  scope: { tenantId: string; storeId: string },
  requiredDomains: readonly ComplianceDomain[],
  serverKillListVersion: string,
  now = new Date(),
) {
  if (!snapshot.encrypted) throw new Error("OFFLINE_SNAPSHOT_NOT_ENCRYPTED");
  if (
    snapshot.tenantId !== scope.tenantId ||
    snapshot.storeId !== scope.storeId
  )
    throw new Error("OFFLINE_SNAPSHOT_SCOPE_MISMATCH");
  if (new Date(snapshot.expiresAt) < now)
    throw new Error("OFFLINE_SNAPSHOT_EXPIRED");
  if (snapshot.emergencyKillListVersion !== serverKillListVersion)
    throw new Error("OFFLINE_KILL_LIST_STALE");
  for (const domain of requiredDomains)
    if (new Date(snapshot.freshnessUntil[domain]) < now)
      throw new Error(`OFFLINE_RULE_STALE:${domain}`);
}

export interface OfflineRiskFacts {
  operation: ClientOperation;
  channel: ClientChannel;
  controlledMedicine?: boolean;
  prescriptionRequired?: boolean;
  customerCredit?: boolean;
  refund?: boolean;
  priceOverride?: boolean;
  eInvoiceRequired?: boolean;
  eWayBillRequired?: boolean;
  stockRiskPaise?: number;
  stockRiskLimitPaise?: number;
}

export function evaluateOfflineOperation(facts: OfflineRiskFacts): {
  decision: OfflineDecision;
  reasons: string[];
} {
  const configured =
    clientCapabilityMatrix[facts.channel][facts.operation].offline;
  const reasons: string[] = [];
  if (configured !== "ALLOW") reasons.push("OPERATION_POLICY");
  if (facts.controlledMedicine || facts.prescriptionRequired)
    reasons.push("REGULATED_ITEM");
  if (facts.customerCredit) reasons.push("CUSTOMER_CREDIT");
  if (facts.refund) reasons.push("REFUND");
  if (facts.priceOverride) reasons.push("PRICE_OVERRIDE");
  if (facts.eInvoiceRequired || facts.eWayBillRequired)
    reasons.push("STATUTORY_PROVIDER_REQUIRED");
  if ((facts.stockRiskPaise ?? 0) > (facts.stockRiskLimitPaise ?? 0))
    reasons.push("STOCK_RISK_LIMIT");
  return {
    decision: reasons.length
      ? configured === "DENY"
        ? "DENY"
        : "REQUIRE_ONLINE"
      : "ALLOW",
    reasons,
  };
}

export type OfflineCommandStatus =
  | "QUEUED"
  | "SENDING"
  | "CONFIRMED"
  | "RETRY"
  | "DEAD_LETTER"
  | "REJECTED";
export interface OfflineCommand {
  localId: string;
  deviceSequence: number;
  channel: ClientChannel;
  operation: ClientOperation;
  endpoint: string;
  tenantId: string;
  storeId: string;
  actorId: string;
  requestHash: string;
  idempotencyKey: string;
  correlationId: string;
  dependsOn: readonly string[];
  localEvidence: Readonly<Record<string, unknown>>;
  payload: Readonly<Record<string, unknown>>;
  status: OfflineCommandStatus;
  attempts: number;
  serverConfirmation?: {
    canonicalId: string;
    version: number;
    confirmedAt: string;
  };
  errorCode?: string;
}

export function validateOfflineQueue(
  commands: readonly OfflineCommand[],
): string[] {
  const errors: string[] = [];
  const ids = new Set<string>();
  let sequence = -1;
  for (const command of commands) {
    if (ids.has(command.localId))
      errors.push(`DUPLICATE_LOCAL_ID:${command.localId}`);
    if (command.deviceSequence <= sequence)
      errors.push(`DEVICE_SEQUENCE_NOT_MONOTONIC:${command.localId}`);
    if (command.status === "CONFIRMED" && !command.serverConfirmation)
      errors.push(`CONFIRMATION_REQUIRED:${command.localId}`);
    if (command.dependsOn.some((dependency) => !ids.has(dependency)))
      errors.push(`DEPENDENCY_NOT_EARLIER:${command.localId}`);
    if (
      command.endpoint !==
      clientCapabilityMatrix[command.channel][command.operation]
        .canonicalEndpoint
    )
      errors.push(`NON_CANONICAL_ENDPOINT:${command.localId}`);
    ids.add(command.localId);
    sequence = command.deviceSequence;
  }
  return errors;
}

export function transitionOfflineCommand(
  command: OfflineCommand,
  next: OfflineCommandStatus,
  confirmation?: OfflineCommand["serverConfirmation"],
  errorCode?: string,
): OfflineCommand {
  const allowed: Record<OfflineCommandStatus, readonly OfflineCommandStatus[]> =
    {
      QUEUED: ["SENDING", "REJECTED"],
      SENDING: ["CONFIRMED", "RETRY", "DEAD_LETTER", "REJECTED"],
      RETRY: ["SENDING", "DEAD_LETTER", "REJECTED"],
      DEAD_LETTER: ["RETRY", "REJECTED"],
      CONFIRMED: [],
      REJECTED: [],
    };
  if (!allowed[command.status].includes(next))
    throw new Error(
      `INVALID_OFFLINE_COMMAND_TRANSITION:${command.status}:${next}`,
    );
  if (next === "CONFIRMED" && !confirmation)
    throw new Error("SERVER_CONFIRMATION_REQUIRED");
  return {
    ...command,
    status: next,
    attempts: next === "SENDING" ? command.attempts + 1 : command.attempts,
    serverConfirmation: confirmation,
    errorCode,
  };
}

export type SyncConflictCode =
  | "STOCK_DEPLETED"
  | "LOT_EXPIRED"
  | "LOT_RECALLED"
  | "PRICE_CHANGED"
  | "TAX_CHANGED"
  | "RULE_CHANGED"
  | "CREDIT_REVOKED"
  | "PERMISSION_REVOKED"
  | "SESSION_CLOSED";
export const syncConflictOutcome: Record<
  SyncConflictCode,
  "REQUOTE" | "REMOVE_LINE" | "ONLINE_REVIEW" | "REAUTHORIZE" | "OPEN_SESSION"
> = {
  STOCK_DEPLETED: "REQUOTE",
  LOT_EXPIRED: "REMOVE_LINE",
  LOT_RECALLED: "REMOVE_LINE",
  PRICE_CHANGED: "REQUOTE",
  TAX_CHANGED: "REQUOTE",
  RULE_CHANGED: "ONLINE_REVIEW",
  CREDIT_REVOKED: "ONLINE_REVIEW",
  PERMISSION_REVOKED: "REAUTHORIZE",
  SESSION_CLOSED: "OPEN_SESSION",
};

export interface FiscalReferenceMapping {
  provisionalReference: string;
  canonicalReference: string;
  providerReference?: string;
  mappedAt: string;
  originalEvidenceHash: string;
}
export function reconcileFiscalReference(
  mapping: FiscalReferenceMapping,
  evidenceHash: string,
) {
  if (mapping.originalEvidenceHash !== evidenceHash)
    throw new Error("PROVISIONAL_EVIDENCE_IMMUTABLE");
  return {
    displayReference: mapping.canonicalReference,
    lineage: [
      mapping.provisionalReference,
      mapping.canonicalReference,
      mapping.providerReference,
    ].filter(Boolean),
  };
}

export type PrinterJobStatus =
  | "QUEUED"
  | "RENDERED"
  | "SENDING"
  | "SENT"
  | "DIALOG_OPENED"
  | "CONFIRMED_PRINTED"
  | "CANCELLED"
  | "FAILED";
export interface PrinterJob {
  idempotencyKey: string;
  templateVersion: string;
  format:
    | "58MM"
    | "80MM"
    | "DOT_MATRIX"
    | "DOT_MATRIX_9_5X5_5"
    | "A5"
    | "A4"
    | "LABEL"
    | "REPORT";
  documentSnapshotHash: string;
  status: PrinterJobStatus;
  attempts: number;
}
export function transitionPrinterJob(
  job: PrinterJob,
  next: PrinterJobStatus,
): PrinterJob {
  const allowed: Record<PrinterJobStatus, readonly PrinterJobStatus[]> = {
    QUEUED: ["RENDERED", "SENDING", "DIALOG_OPENED", "FAILED", "CANCELLED"],
    RENDERED: ["SENDING", "DIALOG_OPENED", "FAILED", "CANCELLED"],
    SENDING: ["SENT", "FAILED"],
    SENT: ["CONFIRMED_PRINTED"],
    DIALOG_OPENED: ["CONFIRMED_PRINTED", "FAILED", "CANCELLED"],
    FAILED: ["SENDING", "DIALOG_OPENED"],
    CONFIRMED_PRINTED: [],
    CANCELLED: [],
  };
  if (!allowed[job.status].includes(next))
    throw new Error(`INVALID_PRINTER_JOB_TRANSITION:${job.status}:${next}`);
  return {
    ...job,
    status: next,
    attempts:
      next === "SENDING" || next === "DIALOG_OPENED"
        ? job.attempts + 1
        : job.attempts,
  };
}

const gtinCheckDigit = (digits: string) => {
  const body = digits.slice(0, -1);
  const sum = [...body]
    .reverse()
    .reduce(
      (total, digit, index) =>
        total + Number(digit) * (index % 2 === 0 ? 3 : 1),
      0,
    );
  return String((10 - (sum % 10)) % 10);
};
export type ScanResult = {
  kind: "GTIN" | "INTERNAL" | "SCHEDULE_H2";
  value: string;
  namespace?: string;
  requiresDisambiguation: boolean;
};
export function parseBarcode(
  value: string,
  options: {
    allowManual: boolean;
    manuallyEntered?: boolean;
    matches?: number;
    h2Namespaces?: readonly string[];
  } = { allowManual: false },
): ScanResult {
  const input = value.trim();
  if (options.manuallyEntered && !options.allowManual)
    throw new Error("BARCODE_MANUAL_ENTRY_FORBIDDEN");
  if (/^\d{8}$|^\d{12,14}$/.test(input)) {
    if (gtinCheckDigit(input) !== input.charAt(input.length - 1))
      throw new Error("GTIN_CHECKSUM_INVALID");
    return {
      kind: "GTIN",
      value: input,
      requiresDisambiguation: (options.matches ?? 1) > 1,
    };
  }
  const h2 = /^H2:([A-Z0-9.-]+):(.+)$/.exec(input);
  if (h2) {
    if (!options.h2Namespaces?.includes(h2[1]))
      throw new Error("H2_QR_NAMESPACE_NOT_ALLOWED");
    return {
      kind: "SCHEDULE_H2",
      namespace: h2[1],
      value: h2[2],
      requiresDisambiguation: false,
    };
  }
  if (!/^[A-Z0-9][A-Z0-9._/-]{2,63}$/i.test(input))
    throw new Error("BARCODE_FORMAT_INVALID");
  return {
    kind: "INTERNAL",
    value: input,
    requiresDisambiguation: (options.matches ?? 1) > 1,
  };
}

export interface MeasurementReading {
  deviceId: string;
  calibratedUntil: string;
  unit: string;
  expectedUnit: string;
  valueMicros: number;
  samplesMicros: readonly number[];
}
export function validateStableMeasurement(
  reading: MeasurementReading,
  now = new Date(),
  toleranceMicros = 1000,
) {
  if (new Date(reading.calibratedUntil) < now)
    throw new Error("MEASUREMENT_DEVICE_CALIBRATION_EXPIRED");
  if (reading.unit !== reading.expectedUnit)
    throw new Error("MEASUREMENT_UNIT_MISMATCH");
  if (
    !reading.samplesMicros.length ||
    Math.max(...reading.samplesMicros) - Math.min(...reading.samplesMicros) >
      toleranceMicros
  )
    throw new Error("MEASUREMENT_READING_UNSTABLE");
  return reading.valueMicros;
}

export type SessionHardwareEvent = {
  type: "SESSION_OPENED" | "CASH_PAYMENT_CONFIRMED" | "SESSION_CLOSED";
  sessionId: string;
  amountPaise?: number;
};
export function hardwareActionsForEvent(
  event: SessionHardwareEvent,
): readonly string[] {
  if (event.type === "CASH_PAYMENT_CONFIRMED")
    return ["OPEN_CASH_DRAWER", "CUSTOMER_DISPLAY_PAYMENT_CONFIRMED"];
  if (event.type === "SESSION_OPENED") return ["CUSTOMER_DISPLAY_SESSION_OPEN"];
  return ["CUSTOMER_DISPLAY_SESSION_CLOSED"];
}

export const INDIA_TIME_ZONE = "Asia/Kolkata" as const;
const INDIA_UTC_OFFSET_MILLISECONDS = 330 * 60 * 1000;

export function startOfIndianDay(value: Date | string) {
  const instant = new Date(value);
  if (Number.isNaN(instant.getTime())) throw new Error("INVALID_INDIAN_DATE");
  const shifted = new Date(instant.getTime() + INDIA_UTC_OFFSET_MILLISECONDS);
  shifted.setUTCHours(0, 0, 0, 0);
  return new Date(shifted.getTime() - INDIA_UTC_OFFSET_MILLISECONDS);
}

export const indiaLocale = {
  formatMoney: (paise: number, locale = "en-IN") =>
    new Intl.NumberFormat(locale, {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 2,
    }).format(paise / 100),
  formatDate: (value: Date | string, locale = "en-IN") =>
    new Intl.DateTimeFormat(locale, {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      timeZone: INDIA_TIME_ZONE,
    }).format(new Date(value)),
  formatDateTime: (value: Date | string, locale = "en-IN") =>
    new Intl.DateTimeFormat(locale, {
      dateStyle: "medium",
      timeStyle: "short",
      hour12: true,
      timeZone: INDIA_TIME_ZONE,
    }).format(new Date(value)),
  formatTime: (value: Date | string, locale = "en-IN") =>
    new Intl.DateTimeFormat(locale, {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
      timeZone: INDIA_TIME_ZONE,
    }).format(new Date(value)),
  businessDate: (value: Date | string) => {
    const parts = Object.fromEntries(
      new Intl.DateTimeFormat("en-CA", {
        timeZone: INDIA_TIME_ZONE,
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
      })
        .formatToParts(new Date(value))
        .map((part) => [part.type, part.value]),
    );
    return `${parts.year}-${parts.month}-${parts.day}`;
  },
} as const;

export interface ClientErrorPresentation {
  message: string;
  retry: "NONE" | "SAME_REQUEST" | "AFTER_REFRESH";
  preventDuplicateSubmit: true;
  correlationId: string;
}
export function presentClientError(error: {
  message: string;
  retryable: boolean;
  action?: string;
  correlationId: string;
}): ClientErrorPresentation {
  return {
    message: error.message,
    retry: !error.retryable
      ? "NONE"
      : error.action === "REFRESH_RESOURCE" || error.action === "REQUOTE"
        ? "AFTER_REFRESH"
        : "SAME_REQUEST",
    preventDuplicateSubmit: true,
    correlationId: error.correlationId,
  };
}

export const rapidBillingKeyboard = {
  focusSearch: "F2",
  focusQuantity: "F4",
  holdCart: "F6",
  takePayment: "F8",
  cancel: "Escape",
} as const;
export const highRiskActionsRequiringConfirmation = [
  "VOID_SALE",
  "REFUND",
  "CLEAR_CART",
  "REMOVE_CONTROLLED_ITEM",
  "CLOSE_SESSION",
] as const;

export interface DeliveryCommand {
  operation: Extract<ClientOperation, `DELIVERY_${string}`>;
  consignmentId: string;
  assignedAgentId: string;
  actorId: string;
  tenantId: string;
  storeId: string;
  evidence?: Readonly<Record<string, unknown>>;
}
export function assertDeliveryCommandScope(
  command: DeliveryCommand,
  actor: { id: string; tenantId: string; storeId: string },
) {
  if (
    command.assignedAgentId !== actor.id ||
    command.actorId !== actor.id ||
    command.tenantId !== actor.tenantId ||
    command.storeId !== actor.storeId
  )
    throw new Error("DELIVERY_COMMAND_SCOPE_DENIED");
  requireClientCapability("DELIVERY_AGENT", command.operation, "ONLINE");
}

export interface ParityScenarioResult {
  scenarioId: string;
  channel: ClientChannel;
  mode: "ONLINE" | "OFFLINE";
  canonicalAssertionHash: string;
  hardware?: string;
  passed: boolean;
}
export function certifyClientParity(
  results: readonly ParityScenarioResult[],
  requiredScenarios: readonly string[],
): string[] {
  const failures: string[] = [];
  for (const scenarioId of requiredScenarios) {
    const scenario = results.filter(
      (result) => result.scenarioId === scenarioId,
    );
    const supportedChannels = clientChannels.filter(
      (channel) => clientCapabilityMatrix[channel].SALE_ACCEPT.online,
    );
    for (const channel of supportedChannels)
      if (
        !scenario.some((result) => result.channel === channel && result.passed)
      )
        failures.push(`MISSING_OR_FAILED:${scenarioId}:${channel}`);
    const hashes = new Set(
      scenario
        .filter((result) => result.passed)
        .map((result) => result.canonicalAssertionHash),
    );
    if (hashes.size > 1)
      failures.push(`CANONICAL_ASSERTION_MISMATCH:${scenarioId}`);
  }
  return failures;
}

export const cliTaskImplementations = {
  "CLI-001": "versioned canonical contracts",
  "CLI-002": "compatibility manifest",
  "CLI-003": "capability matrix",
  "CLI-004": "canonical operation endpoints",
  "CLI-005": "shared desktop policy",
  "CLI-006": "mobile POS channel",
  "CLI-007": "scanner task channel",
  "CLI-008": "projection stamps and query keys",
  "CLI-009": "trusted device assertion",
  "CLI-010": "encrypted scoped snapshot",
  "CLI-011": "offline risk evaluator",
  "CLI-012": "printer job state machine",
  "CLI-013": "barcode parser",
  "CLI-014": "session hardware events",
  "CLI-015": "stable measurement validation",
  "CLI-016": "append-only command contract",
  "CLI-017": "canonical endpoint enforcement",
  "CLI-018": "fiscal reference lineage",
  "CLI-019": "deterministic conflict outcomes",
  "CLI-020": "retry/dead-letter transitions",
  "CLI-021": "error presentation",
  "CLI-022": "keyboard and high-risk confirmation contract",
  "CLI-023": "Indian locale helpers",
  "CLI-024": "parity certification",
  "CLI-025": "delivery command scope",
  "CLI-026": "H2 QR namespace allow-list",
  "CLI-027": "domain freshness and kill-list validation",
} as const satisfies Record<`CLI-${string}`, string>;
